import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  Loader,
  ErrorFallback,
  TextInput,
  InputSwitch,
  DropDownInput,
} from 'smart-react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, CardHeader, CardBody } from '@progress/kendo-react-layout';
import DesktopLayout from '../Components/Layout/DesktopLayout';
import { Button } from '@progress/kendo-react-buttons';
import {
  buildNotification,
  GenerateNotification,
  isValidate,
  isFromValid,
  DescriptionTextArea,
} from 'smart-react';
import { createRole, updateRole } from '../Services/RoleService';
import { isAdminRole } from '../../../../Utils/Auth/ValidatePermissions';
import {
  base64ToObject,
  objectToBase64,
} from '../../../../Utils/Common/commonUtils';
import { useLocation, useNavigate } from 'react-router-dom';
import NoRecordFound from '../../../Core/Components/NoRecordFound/NoRecordFound';
import useQueryParams from '../../../Core/CustomHooks/useQueryParams';
import { Form, Field } from '@progress/kendo-react-form';
import {
  EVENTS_DATA_TYPES,
  NOTIFICATION_TYPES,
} from '../../../../constants/eventDataTypes';
import { Error } from '@progress/kendo-react-labels';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../../Core/Context/AuthContext';
import NoPermission from '../../../Core/NoPermission/NoPermission';
import { getRole, getRoleCategories } from '../Services/RoleService';
import { SplitButton, SplitButtonItem } from '@progress/kendo-react-buttons';
import {
  CREATE_MESSAGE,
  UPDATE_MESSAGE,
  ERROR_MESSAGE,
} from '../../../../constants/notificationMessages';
/**
 * Add New Admin Role main screen
 * @type {React.FC<Props>}
 * @returns {React.ReactElement} The Add New Admin Role component.
 */
const AddRole = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { getParam } = useQueryParams();
  const refId = getParam('refId');
  const { user } = useAuth(); // Get tenantID using Auth Context
  /**
   * Used to handle multiple tabs
   */
  const [detailsDataModified, setDetailsDataModified] = React.useState(false);
  const [isTextArea, setIsTextArea] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [selected, setSelected] = React.useState(0);
  const [shouldUpdate, setShouldUpdate] = React.useState(false);
  const [formData, setFormData] = React.useState({
    RoleId: '',
    Name: '',
    Desc: '',
    RoleCatId: '',
    RoleCatName: '',
    Selected: true,
    Active: true,
  });
  const [roleCategories, setRoleCategories] = React.useState([]);
  const [validateForm, setValidateForm] = React.useState(false);
  const requiredFields = ['Name', 'RoleId'];
  const [editorField, setEditorField] = React.useState({
    name: 'Desc',
    value: formData?.Desc,
    label: 'Description',
  });
  const [noRecord, setNoRecord] = React.useState(false);
  const [tempState, setTempState] = React.useState(state);

  /**
   * Get Role Detail from API for edit Role
   * @param {*} stateData
   */
  const fetchRoleData = async (stateData) => {
    setLoading(true);
    try {
      let refObj = base64ToObject(refId);
      const response = await getRole({
        data: refObj,
      });
      const responseData = response?.Payload;
      if (responseData) {
        setTempState({ ...stateData, dataSet: responseData, edit: true });
        setFormData(responseData);
        setNoRecord(false);
        setDetailsDataModified(true);
      } else {
        setNoRecord(true);
      }
    } catch (error) {
      // Handle error state if needed
      setNoRecord(true);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (tempState || (refId && refId !== 'new')) {
      const dataSet = state ? state.dataSet : null;
      // Call the async function
      fetchRoleData(state);

      if (dataSet) {
        // Rest of the synchronous logic
        setFormData({ ...dataSet });
        setDetailsDataModified(true);
      }
    } else {
      setDetailsDataModified(true);
    }
  }, [state]);

  /**
   * Get Role Categories from API for edit Profile Group
   */
  React.useEffect(() => {
    (async () => {
      setLoading(true);
      const tenantResponse = await getRoleCategories({});
      setRoleCategories(tenantResponse?.Payload);
      setLoading(false);
    })();
  }, []);

  /**
   * handle Input Change
   * @e object
   */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? (checked ? 1 : 0) : value;
    setFormData((prevState) => ({
      ...prevState,
      [name]: newValue,
    }));
  };

  /**
   * Handle Selected Tab
   * @param {*} e
   */
  const handleSelect = (e) => {
    setSelected(e.selected);
    setShouldUpdate(!shouldUpdate);
  };

  /**
   * onSave
   * submit the organization data to api
   */
  const onSave = async ({ isExit = false, isContinue = false }) => {
    setValidateForm(true);
    if (!isFromValid({ formData, requiredFields })) {
      return;
    }

    setLoading(true);
    let notificationMetaData = {};
    const data = {
      ...formData,
    };

    let response;
    let method;

    if (tempState?.edit) {
      method = updateRole;
      notificationMetaData = UPDATE_MESSAGE;
    } else {
      method = createRole;
      notificationMetaData = CREATE_MESSAGE;
      setFormData({
        RoleId: '',
        Name: '',
        Desc: '',
        RoleCatId: '',
        RoleCatName: '',
        Selected: true,
        Active: true,
      });
    }

    try {
      setValidateForm(false);
      response = await method({
        data,
        isServiceWorker: false,
      });

      if (!response.IsSuccess) {
        GenerateNotification(
          buildNotification({
            title: response?.Title,
            description: response.Message,
            style: response?.Style,
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
      } else {
        GenerateNotification(
          buildNotification(notificationMetaData),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
        const responseOrg = await getRole({
          data: response?.Payload,
        });
        const testData = responseOrg?.Payload;
        // Update tempState to trigger rerender
        setTempState({
          ...tempState,
          dataSet: testData,
        });
        if (isContinue) {
          const refId = objectToBase64({
            RoleId: testData?.RoleId,
          });
          navigate(`/admin/role?refId=${refId}`, {
            state: {
              edit: true,
              dataSet: testData,
            },
          });
        }
        if (isExit) {
          navigate('/admin/roles');
        }
      }
    } catch (error) {
      GenerateNotification(
        buildNotification(ERROR_MESSAGE),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * onExit
   * back to organizations listing screen
   */
  const onExit = () => {
    navigate('/admin/roles');
  };

  const handleSaveSplit = (e) => {
    const { itemIndex } = e;
    switch (itemIndex) {
      case 0: {
        onSave({ isExit: true });
        break;
      }
      case 1:
        onSave({ isContinue: true });
      default: {
      }
    }
  };

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // reset the state of your app so the error doesn't happen again
      }}
    >
      <div className='container mx-auto'>
        {!isAdminRole(user) && <NoPermission />}
        {isAdminRole(user) && (
          <React.Suspense fallback={<Loader />}>
            {loading && <Loader />}
            {noRecord ? (
              <NoRecordFound
                recordNotFoundText={'No Data found'}
                onExit={onExit}
              />
            ) : (
              <>
                <div className={`${isTextArea ? '' : 'k-d-none'}`}>
                  <DescriptionTextArea
                    onChange={handleInputChange}
                    label={'Description'}
                    renderTypes={!detailsDataModified ? 'ReadOnly' : ''}
                    validateForm={validateForm}
                    formData={formData}
                    setFormData={setFormData}
                    detailsDataModified={detailsDataModified}
                    isTextArea={isTextArea}
                    setIsTextArea={setIsTextArea}
                    editorField={editorField}
                    setEditorField={setEditorField}
                    row={2}
                  />
                </div>
                <div
                  className={`add-rf-profile-group main-page-section add-page-section ${!isTextArea ? '' : 'k-d-none'}`}
                >
                  <Card className='tophead'>
                    <CardHeader>
                      <div className='action-buttons-container'>
                        <Link
                          className='action-icon-wrapper !k-d-block'
                          to={`/admin/roles`}
                        >
                          <FontAwesomeIcon
                            className='k-mr-2 k-pt-3'
                            icon={faArrowLeft}
                          />
                        </Link>
                        <div>
                          <p className='display-text k-mb-0'>
                            Role: {formData?.Name}
                          </p>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                  <Card className='k-mt-3 detail-card'>
                    <CardHeader>
                      <h6 className='card-title-secondary'>Details</h6>
                    </CardHeader>
                    <CardBody className='detail-card-body'>
                      <div>
                        <Form
                          onSubmit={onSave}
                          render={(formRenderProps) => (
                            <div className='card-content'>
                              <div className='k-d-flex single-field-row'>
                                <div
                                  className={`field-wrapper ${isValidate(validateForm, formData?.RoleId) ? 'field-invalid' : 'field-valid'}`}
                                >
                                  <Field
                                    key={'RoleId'}
                                    id={'RoleId'}
                                    name={'RoleId'}
                                    data={formData?.RoleId}
                                    onChange={handleInputChange}
                                    label={'Role Id'}
                                    type={'text'}
                                    component={TextInput}
                                    renderTypes={tempState?.edit ? 'ReadOnly' : ''}
                                    optional={false}
                                    requiredField={true}
                                  />
                                  {isValidate(
                                    validateForm,
                                    formData?.RoleId,
                                  ) && (
                                    <Error>This field cannot be empty</Error>
                                  )}
                                </div>
                              </div>
                              <div className='k-d-flex single-field-row'>
                                <div
                                  className={`field-wrapper ${isValidate(validateForm, formData?.Name) ? 'field-invalid' : 'field-valid'}`}
                                >
                                  <Field
                                    key={'Name'}
                                    id={'Name'}
                                    name={'Name'}
                                    data={formData?.Name}
                                    onChange={handleInputChange}
                                    label={'Name'}
                                    type={'text'}
                                    component={TextInput}
                                    renderTypes={
                                      !detailsDataModified ? 'ReadOnly' : ''
                                    }
                                    optional={false}
                                    requiredField={true}
                                  />
                                  {isValidate(validateForm, formData?.Name) && (
                                    <Error>This field cannot be empty</Error>
                                  )}
                                </div>
                                <div className={`field-wrapper`}>
                                  <DropDownInput
                                    label='Role Category'
                                    textField='Name'
                                    dataItemKey='RoleCatId'
                                    name={'RoleCatId'}
                                    dataSet={roleCategories}
                                    value={
                                      formData?.RoleCatId
                                        ? roleCategories?.filter(
                                            (userTenant) =>
                                              userTenant.RoleCatId ===
                                              formData?.RoleCatId,
                                          )[0]
                                        : null
                                    }
                                    handleChange={(e) => {
                                      setFormData((prevState) => ({
                                        ...prevState,
                                        RoleCatId: e.value?.RoleCatId,
                                        RoleCatName: e.value?.Name,
                                      }));
                                    }}
                                    defaultItem={{
                                      Name: 'Select Role Category ...',
                                      RoleCatId: '',
                                    }}
                                    filterable={true}
                                  />
                                </div>
                              </div>
                              <div className='k-d-flex single-field-row'>
                                <div
                                  className={`field-wrapper field-wrapper-description field-wrapper-100`}
                                >
                                  <DescriptionTextArea
                                    onChange={handleInputChange}
                                    label={'Description'}
                                    renderTypes={
                                      !detailsDataModified ? 'ReadOnly' : ''
                                    }
                                    validateForm={validateForm}
                                    formData={formData}
                                    setFormData={setFormData}
                                    detailsDataModified={detailsDataModified}
                                    isTextArea={isTextArea}
                                    setIsTextArea={setIsTextArea}
                                    editorField={{
                                      name: 'Desc',
                                      value: formData?.Desc,
                                      label: 'Description',
                                    }}
                                    setEditorField={setEditorField}
                                    row={2}
                                  />
                                </div>
                              </div>
                              <div className='single-field-row k-mt-4 single-row-60'>
                                <div className={`field-wrapper`}>
                                  <Field
                                    name='Active'
                                    component={InputSwitch}
                                    label='Active'
                                    onLabel='Yes'
                                    offLabel='No'
                                    id='Active'
                                    checked={formData?.Active}
                                    onChange={handleInputChange}
                                    disabled={!detailsDataModified}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        />
                      </div>
                    </CardBody>
                  </Card>
                  {detailsDataModified && (
                    <Card className='k-mt-3'>
                      <CardHeader>
                        <div className='k-d-flex single-field-row button-form-wrapper k-action-buttons !k-px-0'>
                          <>
                            <Button
                              icon={'cancel'}
                              onClick={() => onExit()}
                              type='button'
                            >
                              Cancel
                            </Button>
                            <SplitButton
                              text='save'
                              themeColor={'primary'}
                              onButtonClick={() =>
                                onSave({ isExit: false, isContinue: true })
                              }
                              onItemClick={handleSaveSplit}
                            >
                              <SplitButtonItem text='Save & Close' />
                            </SplitButton>
                          </>
                        </div>
                      </CardHeader>
                    </Card>
                  )}
                  {tempState?.edit && (
                    <Card className='k-mt-3 detail-grid-section'>
                      <CardBody className='!k-px-0'>
                        <DesktopLayout
                          handleSelect={handleSelect}
                          selected={selected}
                          detailsDataModified={detailsDataModified}
                          state={tempState}
                          shouldUpdate={shouldUpdate}
                          setShouldUpdate={setShouldUpdate}
                          roleCategories={roleCategories}
                        />
                      </CardBody>
                    </Card>
                  )}
                </div>
              </>
            )}
          </React.Suspense>
        )}
      </div>
    </ErrorBoundary>
  );
};
export default AddRole;
